import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import { S, A, DARK } from '~/app/ui/tokens';

interface Props {
  headerDepth: number;
  headerPrefix: string;
  label: string;
  isHidden: boolean;
  toggleHidden: () => void;
}

export default function EditComponentStickyHeader(props: Props) {
  const { headerDepth, headerPrefix, label, isHidden, toggleHidden } = props;

  const ref = useRef();
  const [isPinned, setIsPinned] = useState(false);

  useEffect(() => {
      // Check if indicator is partially outside of the container to detect "pinned" status
      const observer = new IntersectionObserver(
        ([e]) => {
          setIsPinned(e.intersectionRatio < 1);
        },
        { threshold: [1] }
      );

      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
  }, []);

  return (
    <Container isPinned={isPinned} headerDepth={headerDepth}>
      <StickyIndicator ref={ref} isPinned={isPinned} />
      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <span>{isPinned ? `${headerPrefix}${label}` : label}</span>
        <i className="material-icons" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={toggleHidden}>
          {isHidden ? 'expand_more' : 'expand_less'}
        </i>
      </div>
    </Container>
  );
}

const Container = styled(Box)<{ headerDepth: number, isPinned: boolean }>(({ headerDepth, isPinned }) => ({
  marginBottom: '20px',
  fontWeight: '500',
  fontSize: '18px',
  padding: '0px 10px 10px 0px',
  color: 'hsl(0, 0%, 40%)',
  borderBottom: '1px solid hsl(0, 0%, 90%)',

  // Sticky header
  position: 'sticky',
  top: '0px',

  // Cover background elements - move right to cover parent header completely
  width: isPinned ? `calc(100% + ${(headerDepth || 0) * 61}px)` : '100%',
  backgroundColor: 'rgb(250,250,250)',

  // Move left if pinned
  marginLeft: isPinned && `${(headerDepth || 0) * -41}px`,

  [DARK]: { color: S.muted, borderBottomColor: S.line, backgroundColor: A.surface }
}));


const StickyIndicator = styled(Box)<{ isPinned: boolean }>(({ isPinned }) => ({
  position: 'absolute',
  top: '-25px',
  height: '25px',
  width: '100%',

  // Cover background elements
  backgroundColor: isPinned && 'rgb(250,250,250)',
  [DARK]: { backgroundColor: isPinned && A.surface }
}));
